import { useEffect } from 'react';
import styled from 'styled-components';
import { mqLargeMin } from 'styled/tools/media-queries';
import { createMarkup } from 'lib/utils/create-markup';
import Icon from 'components/global/Icon/Primary';

const Sidebar = styled.div`
  position: fixed;
  top: 0;
  left: 100vw;
  bottom: 0;
  height: 100vh;
  transform: translateX(0%);
  transition: transform 0.3s;
  padding: 0 1.5rem 4rem;
  width: 33rem;
  max-width: 100vw;
  background-color: var(--fill-secondary);
  z-index: 10;
  overflow: auto;

  @media ${mqLargeMin} {
    padding: 0 3rem 4rem;
  }

  *:not(img) {
    color: var(--fill-brand);
  }

  &.is-open {
    transform: translateX(-100%);
  }
`;

const SidebarHeader = styled.div`
  position: sticky;
  top: 0;
  border-bottom: 1px solid var(--fill-brand);
  padding: 3rem 0 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: var(--fill-brand);
  background-color: var(--fill-secondary);
  font-size: 18px;
  font-weight: 500;

  *:not(img) {
    color: var(--fill-brand);
  }
`;

const SidebarContent = styled.div`
  padding-top: 1.5rem;
  display: flex;
  flex-direction: column;

  @media ${mqLargeMin} {
    padding-top: 3rem;
  }
  gap: 0.75rem;

  * {
    color: var(--fill-brand);
  }

  h2 {
    margin-bottom: 0.5rem;
    font-size: 24px;
    font-weight: 400;
    line-height: 32px;
  }

  h2 strong {
    font-size: 24px;
  }

  h3 {
    margin-top: 0.5rem;
    border-top: 1px dashed var(--fill-brand);
    padding-top: 1.5rem;
    font-size: 18px;
    font-weight: 500;
    line-height: 28px;
  }

  h4 {
    font-size: 16px;
    line-height: 22.4px;
    font-weight: 500;
  }

  ul {
    list-style-type: disc;
    list-style-position: outside;
    padding-left: 1.5em;
  }
`;

const SidebarCloseButton = styled.button`
  appearance: none;
  border: 0;
  background-color: transparent;
  cursor: pointer;
  font-size: 0;
`;

const CLinicalTrials = ({
  productInfo,
  isOpen,
  setIsOpen,
  contents,
  children,
}) => {
  useEffect(() => {
    const header = document.querySelector<HTMLElement>('header[role="banner"]');
    const body = document.body;

    if (isOpen) {
      if (!header?.style || !body?.style) {
        return;
      }
      // force open after any other sidebars close
      const openTimer = setTimeout(() => {
        header.style.display = 'none';
        body.style.overflow = 'hidden';
      }, 200);
      return () => clearTimeout(openTimer);
    } else {
      const closeTimer = setTimeout(() => {
        if (header) header.style.display = 'block';
        body.style.removeProperty('overflow');
      }, 100);
      return () => clearTimeout(closeTimer);
    }
  }, [isOpen]);

  if (!productInfo || (!children && !contents)) {
    return null;
  }

  try {
    return (
      <Sidebar className={`${isOpen ? 'is-open' : ''}`} aria-hidden={!isOpen}>
        <SidebarHeader>
          {productInfo.title}
          <SidebarCloseButton
            aria-label='close'
            onClick={() => setIsOpen(false)}
          >
            <Icon name='close' size='md' />
          </SidebarCloseButton>
        </SidebarHeader>
        {isOpen && !children && (
          <SidebarContent dangerouslySetInnerHTML={createMarkup(contents)} />
        )}
        {isOpen && children && <SidebarContent>{children}</SidebarContent>}
      </Sidebar>
    );
  } catch {
    return null;
  }
};

export default CLinicalTrials;
